// 메뉴·레시피 Zustand 스토어 — Supabase 영속화 + localStorage 폴백
// 진실 소스: Supabase `menus` (RLS: store_id = auth.uid())
// 메뉴 항목은 개별 행으로 저장 (JSONB 블롭 아님)
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from './authStore'
import type { MenuItem } from '@/types/menu'

// 재료비 합계 계산 유틸
export function calcTotalCost(ingredients: MenuItem['ingredients']): number {
  return ingredients.reduce((sum, ing) => {
    // 단가(원/100g 기준으로 정규화) x 사용량
    return sum + (ing.unitPrice * ing.amount) / 100
  }, 0)
}

// 인증된 사용자 ID
function getStoreId(): string | null {
  return useAuthStore.getState().user?.id ?? null
}

// 샘플 데이터 ID 접두사 — 사용자 데이터와 구분
const SAMPLE_MENU_IDS = new Set(['menu-01', 'menu-02', 'menu-03', 'menu-04', 'menu-05'])

// ─── 샘플 메뉴 5개 — 비로그인 폴백용 ───
const SAMPLE_MENUS: MenuItem[] = [
  {
    id: 'menu-01',
    name: '시그니처 아이스 아메리카노',
    category: 'coffee',
    ingredients: [
      { id: 'ing-01-1', name: '에스프레소 원두', unitPrice: 80, amount: 18, unit: 'g' },
      { id: 'ing-01-2', name: '얼음', unitPrice: 5, amount: 200, unit: 'g' },
      { id: 'ing-01-3', name: '정수', unitPrice: 1, amount: 200, unit: 'ml' },
    ],
    totalCost: 80 * 18 / 1000 * 1000 + 5 * 0.2 + 1 * 0.2,
    salePrice: 5500,
    notes: '더블샷 기본 제공, 원두: 에티오피아+콜롬비아 블렌드',
    createdAt: '2024-03-01',
  },
  {
    id: 'menu-02',
    name: '카라멜 마키아토',
    category: 'coffee',
    ingredients: [
      { id: 'ing-02-1', name: '에스프레소 원두', unitPrice: 80, amount: 18, unit: 'g' },
      { id: 'ing-02-2', name: '우유', unitPrice: 3, amount: 200, unit: 'ml' },
      { id: 'ing-02-3', name: '카라멜 시럽', unitPrice: 15, amount: 20, unit: 'ml' },
      { id: 'ing-02-4', name: '바닐라 시럽', unitPrice: 12, amount: 10, unit: 'ml' },
    ],
    totalCost: 1440 + 600 + 300 + 120,
    salePrice: 6000,
    notes: '스팀밀크 → 에스프레소 순으로 부어 마블 연출',
    createdAt: '2024-03-01',
  },
  {
    id: 'menu-03',
    name: '딸기 케이크',
    category: 'dessert',
    ingredients: [
      { id: 'ing-03-1', name: '국내산 딸기', unitPrice: 25, amount: 100, unit: 'g' },
      { id: 'ing-03-2', name: '생크림', unitPrice: 8, amount: 80, unit: 'ml' },
      { id: 'ing-03-3', name: '스펀지 케이크', unitPrice: 6, amount: 120, unit: 'g' },
      { id: 'ing-03-4', name: '설탕', unitPrice: 2, amount: 30, unit: 'g' },
    ],
    totalCost: 2500 + 640 + 720 + 60,
    salePrice: 7500,
    notes: '시즌 한정 메뉴 (2~4월), 당일 제조 원칙',
    createdAt: '2024-03-01',
  },
  {
    id: 'menu-04',
    name: '아보카도 토스트',
    category: 'food',
    ingredients: [
      { id: 'ing-04-1', name: '아보카도', unitPrice: 18, amount: 100, unit: 'g' },
      { id: 'ing-04-2', name: '사워도우 빵', unitPrice: 10, amount: 80, unit: 'g' },
      { id: 'ing-04-3', name: '달걀', unitPrice: 5, amount: 50, unit: 'g' },
      { id: 'ing-04-4', name: '레몬즙', unitPrice: 8, amount: 10, unit: 'ml' },
      { id: 'ing-04-5', name: '올리브오일', unitPrice: 20, amount: 5, unit: 'ml' },
    ],
    totalCost: 1800 + 800 + 250 + 80 + 100,
    salePrice: 8500,
    notes: '반숙 달걀 옵션 가능, 플레이팅 미세 소금 추가',
    createdAt: '2024-03-01',
  },
  {
    id: 'menu-05',
    name: '말차 라떼',
    category: 'non_coffee',
    ingredients: [
      { id: 'ing-05-1', name: '말차 파우더', unitPrice: 50, amount: 8, unit: 'g' },
      { id: 'ing-05-2', name: '우유', unitPrice: 3, amount: 250, unit: 'ml' },
      { id: 'ing-05-3', name: '설탕 시럽', unitPrice: 5, amount: 15, unit: 'ml' },
    ],
    totalCost: 400 + 750 + 75,
    salePrice: 6500,
    notes: '아이스/핫 선택 가능, 오트밀크 변경 시 +500원',
    createdAt: '2024-03-01',
  },
]

interface MenuState {
  menus: MenuItem[]
  isSynced: boolean
  loading: boolean

  // Supabase 동기화
  syncFromServer: () => Promise<void>

  // CRUD
  addMenu: (menu: Omit<MenuItem, 'id' | 'totalCost' | 'createdAt'>) => Promise<string>
  updateMenu: (id: string, updates: Partial<MenuItem>) => Promise<void>
  deleteMenu: (id: string) => Promise<void>
  setRecipe: (id: string, recipe: string) => Promise<void>
}

export const useMenuStore = create<MenuState>()(
  persist(
    (set, get) => ({
      menus: SAMPLE_MENUS,
      isSynced: false,
      loading: false,

      // ─── 서버 → 클라이언트 동기화 ───
      syncFromServer: async () => {
        const storeId = getStoreId()
        if (!storeId) return

        set({ loading: true })
        try {
          const { data, error } = await supabase
            .from('menus')
            .select('*')
            .eq('store_id', storeId)
            .order('created_at', { ascending: true })
          if (error) throw error

          // DB → 프론트엔드 타입 변환
          const menus: MenuItem[] = (data ?? []).map((row: Record<string, unknown>) => ({
            id: row.id as string,
            name: row.name as string,
            category: (row.category as MenuItem['category']) ?? 'other',
            ingredients: (row.ingredients_json as MenuItem['ingredients']) ?? [],
            totalCost: Number(row.total_cost ?? 0),
            salePrice: Number(row.sale_price ?? 0),
            photoUrl: (row.photo_url as string) ?? '',
            recipe: (row.recipe as string) ?? '',
            notes: (row.notes as string) ?? '',
            createdAt: row.created_at
              ? new Date(row.created_at as string).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0],
          }))

          set({
            menus: menus.length > 0 ? menus : SAMPLE_MENUS,
            isSynced: true,
          })
        } catch (e) {
          console.error('[menuStore] syncFromServer 실패:', e)
        } finally {
          set({ loading: false })
        }
      },

      // ─── 메뉴 추가 ───
      addMenu: async (menuData) => {
        const id = `menu-${Date.now()}`
        const totalCost = calcTotalCost(menuData.ingredients)
        const createdAt = new Date().toISOString().split('T')[0]
        const newMenu: MenuItem = { ...menuData, id, totalCost, createdAt }

        // optimistic UI
        set((s) => ({ menus: [...s.menus, newMenu] }))

        const storeId = getStoreId()
        if (!storeId) return id

        try {
          const { data, error } = await supabase
            .from('menus')
            .insert({
              store_id: storeId,
              name: newMenu.name,
              category: newMenu.category,
              ingredients_json: newMenu.ingredients,
              total_cost: totalCost,
              sale_price: newMenu.salePrice,
              photo_url: newMenu.photoUrl ?? '',
              recipe: newMenu.recipe ?? '',
              notes: newMenu.notes ?? '',
            })
            .select()
            .single()
          if (error) throw error

          // 임시 ID → DB ID로 교체
          const dbId = data.id as string
          set((s) => ({
            menus: s.menus.map((m) => (m.id === id ? { ...m, id: dbId } : m)),
          }))
          return dbId
        } catch (e) {
          console.error('[menuStore] addMenu 실패:', e)
          // 롤백
          set((s) => ({ menus: s.menus.filter((m) => m.id !== id) }))
          return id
        }
      },

      // ─── 메뉴 수정 ───
      updateMenu: async (id, updates) => {
        const prev = get().menus
        // optimistic
        set((s) => ({
          menus: s.menus.map((m) => {
            if (m.id !== id) return m
            const updated = { ...m, ...updates }
            if (updates.ingredients) {
              updated.totalCost = calcTotalCost(updates.ingredients)
            }
            return updated
          }),
        }))

        const storeId = getStoreId()
        if (!storeId || id.startsWith('menu-')) return

        try {
          const dbUpdates: Record<string, unknown> = {}
          if (updates.name !== undefined) dbUpdates.name = updates.name
          if (updates.category !== undefined) dbUpdates.category = updates.category
          if (updates.ingredients !== undefined) {
            dbUpdates.ingredients_json = updates.ingredients
            dbUpdates.total_cost = calcTotalCost(updates.ingredients)
          }
          if (updates.salePrice !== undefined) dbUpdates.sale_price = updates.salePrice
          if (updates.photoUrl !== undefined) dbUpdates.photo_url = updates.photoUrl
          if (updates.recipe !== undefined) dbUpdates.recipe = updates.recipe
          if (updates.notes !== undefined) dbUpdates.notes = updates.notes

          if (Object.keys(dbUpdates).length > 0) {
            const { error } = await supabase
              .from('menus')
              .update(dbUpdates)
              .eq('id', id)
            if (error) throw error
          }
        } catch (e) {
          console.error('[menuStore] updateMenu 실패:', e)
          set({ menus: prev }) // 롤백
        }
      },

      // ─── 메뉴 삭제 ───
      deleteMenu: async (id) => {
        const prev = get().menus
        set((s) => ({ menus: s.menus.filter((m) => m.id !== id) }))

        const storeId = getStoreId()
        if (!storeId || id.startsWith('menu-')) return

        try {
          const { error } = await supabase
            .from('menus')
            .delete()
            .eq('id', id)
          if (error) throw error
        } catch (e) {
          console.error('[menuStore] deleteMenu 실패:', e)
          set({ menus: prev })
        }
      },

      // ─── 레시피 설정 ───
      setRecipe: async (id, recipe) => {
        const prev = get().menus
        set((s) => ({
          menus: s.menus.map((m) => (m.id === id ? { ...m, recipe } : m)),
        }))

        const storeId = getStoreId()
        if (!storeId || id.startsWith('menu-')) return

        try {
          const { error } = await supabase
            .from('menus')
            .update({ recipe })
            .eq('id', id)
          if (error) throw error
        } catch (e) {
          console.error('[menuStore] setRecipe 실패:', e)
          set({ menus: prev })
        }
      },
    }),
    { name: 'cafe-brain-menus' }
  )
)

// ─── 샘플 데이터 여부 셀렉터 ───
// 모든 메뉴가 초기 샘플 ID에 해당하면 샘플 상태로 판단
export function useMenuIsSampleData(): boolean {
  const menus = useMenuStore((s) => s.menus)
  if (menus.length === 0) return false
  return menus.every((m) => SAMPLE_MENU_IDS.has(m.id))
}

// ─── 인증 상태 변화 자동 반응 ───
if (typeof window !== 'undefined') {
  let lastUserId: string | null = useAuthStore.getState().user?.id ?? null

  if (lastUserId) {
    void useMenuStore.getState().syncFromServer()
  }

  useAuthStore.subscribe((state) => {
    const newId = state.user?.id ?? null
    if (newId === lastUserId) return

    if (newId) {
      void useMenuStore.getState().syncFromServer()
    } else {
      // 로그아웃 — 샘플 데이터로 리셋
      useMenuStore.setState({
        menus: SAMPLE_MENUS,
        isSynced: false,
        loading: false,
      })
    }
    lastUserId = newId
  })
}
